import { useEffect, useRef, useState } from "react";
import { MapContainer, TileLayer, Marker, Popup, useMap, useMapEvents } from "react-leaflet";
import L from "leaflet";
import "leaflet/dist/leaflet.css";
import { Button } from "@/components/ui/button";
import {
  Loader2, Save, X, Crosshair, MapPin, Layers, Move, Maximize2,
} from "lucide-react";

// Fix default icon paths (Leaflet + Webpack quirk)
const defaultIcon = L.icon({
  iconUrl: "https://unpkg.com/leaflet@1.9.4/dist/images/marker-icon.png",
  iconRetinaUrl: "https://unpkg.com/leaflet@1.9.4/dist/images/marker-icon-2x.png",
  shadowUrl: "https://unpkg.com/leaflet@1.9.4/dist/images/marker-shadow.png",
  iconSize: [25, 41],
  iconAnchor: [12, 41],
  popupAnchor: [1, -34],
  shadowSize: [41, 41],
});
L.Marker.prototype.options.icon = defaultIcon;

// Smaller red marker for "neighboring already-tagged" houses — distinct from active pin.
const neighborIcon = L.divIcon({
  className: "neighbor-pin",
  html: `
    <div style="
      width:18px;height:18px;border-radius:50%;
      background:#ef4444;border:2.5px solid #fff;
      box-shadow:0 0 0 1.5px #b91c1c, 0 2px 6px rgba(0,0,0,.45);
    "></div>`,
  iconSize: [18, 18],
  iconAnchor: [9, 9],
  popupAnchor: [0, -10],
});

/** Imperatively fit map bounds to all provided coords (including current pin). */
function FitAllControl({ trigger, coords }) {
  const map = useMap();
  useEffect(() => {
    if (!trigger) return;
    if (!Array.isArray(coords) || coords.length === 0) return;
    try {
      const bounds = L.latLngBounds(coords.map(c => [c.lat, c.lng]));
      if (bounds.isValid()) {
        map.fitBounds(bounds, { padding: [40, 40], maxZoom: 19 });
      }
    } catch (e) { /* ignore */ }
  }, [trigger, coords, map]);
  return null;
}

function RecenterControl({ lat, lng }) {
  const map = useMap();
  const didInit = useRef(false);
  useEffect(() => {
    if (lat != null && lng != null && !didInit.current) {
      map.setView([lat, lng], 19, { animate: true });
      didInit.current = true;
    }
  }, [lat, lng, map]);
  return null;
}

/** Ensures Leaflet recalculates container size once modal is fully rendered. */
function InvalidateOnMount() {
  const map = useMap();
  useEffect(() => {
    const t1 = setTimeout(() => map.invalidateSize(), 100);
    const t2 = setTimeout(() => map.invalidateSize(), 400);
    const t3 = setTimeout(() => map.invalidateSize(), 1000);
    return () => { clearTimeout(t1); clearTimeout(t2); clearTimeout(t3); };
  }, [map]);
  return null;
}

/** Tap-to-move: tap anywhere on the map to move the marker there (only in non-readonly mode). */
function ClickToMove({ onMove, disabled }) {
  useMapEvents({
    click(e) {
      if (disabled) return;
      onMove({ lat: e.latlng.lat, lng: e.latlng.lng });
    },
  });
  return null;
}

/**
 * MapPreviewModal
 * - Shows a satellite map with a draggable marker so the petugas
 *   can adjust the saved pin to the exact rooftop.
 * - Also supports read-only "viewer" mode for displaying a saved location.
 *
 * Props:
 *  - open (bool)
 *  - initialLat, initialLng, initialAccuracy
 *  - onCancel()
 *  - onConfirm({ latitude, longitude, accuracy }) -> Promise (optional in readonly)
 *  - title (string)
 *  - confirmLabel (string)
 *  - saving (bool)
 *  - readonly (bool)   // if true → marker not draggable, no Save button
 *  - subtitle (string) // optional, shown under title
 */
export default function MapPreviewModal({
  open,
  initialLat,
  initialLng,
  initialAccuracy = null,
  onCancel,
  onConfirm,
  title = "Sesuaikan Lokasi (Mode Satelit)",
  confirmLabel = "Simpan Lokasi",
  saving = false,
  readonly = false,
  subtitle = null,
  nearby = [],
  enableFitAll = false,
  onDetailClick = null,
}) {
  const [pos, setPos] = useState({
    lat: initialLat ?? null,
    lng: initialLng ?? null,
    accuracy: initialAccuracy,
  });
  const [layer, setLayer] = useState("satellite"); // 'satellite' | 'street'
  const [locking, setLocking] = useState(false);
  const [fitAllNonce, setFitAllNonce] = useState(0);
  const markerRef = useRef(null);

  useEffect(() => {
    if (open) {
      setPos({
        lat: initialLat ?? null,
        lng: initialLng ?? null,
        accuracy: initialAccuracy,
      });
    }
  }, [open, initialLat, initialLng, initialAccuracy]);

  if (!open) return null;
  if (pos.lat == null || pos.lng == null) return null;

  const useMyGPS = () => {
    if (!navigator.geolocation) return;
    setLocking(true);
    navigator.geolocation.getCurrentPosition(
      (p) => {
        setPos({
          lat: p.coords.latitude,
          lng: p.coords.longitude,
          accuracy: p.coords.accuracy,
        });
        setLocking(false);
      },
      () => setLocking(false),
      { enableHighAccuracy: true, timeout: 15000, maximumAge: 0 }
    );
  };

  const handleDragEnd = () => {
    const m = markerRef.current;
    if (!m) return;
    const { lat, lng } = m.getLatLng();
    setPos((prev) => ({ ...prev, lat, lng, accuracy: null })); // manual move → unknown accuracy
  };

  return (
    <div
      className="fixed inset-0 z-[120] bg-black/75 flex items-end sm:items-center justify-center"
      data-testid="map-preview-modal"
    >
      <div className="bg-white w-full sm:rounded-2xl sm:max-w-2xl shadow-2xl flex flex-col overflow-hidden" style={{ height: "min(95vh, 100dvh)" }}>
        {/* Header */}
        <div className="bg-gradient-to-r from-cyan-600 to-emerald-600 text-white p-3 flex items-center justify-between flex-shrink-0">
          <div className="flex items-center gap-2">
            <MapPin className="w-5 h-5" />
            <div>
              <div className="font-semibold text-sm">{title}</div>
              <div className="text-[11px] opacity-90 flex items-center gap-1">
                {readonly
                  ? (subtitle || "Tampilan satelit lokasi tersimpan")
                  : (<><Move className="w-3 h-3" /> Tap atau geser pin ke atap rumah pelanggan</>)
                }
              </div>
            </div>
          </div>
          <button
            onClick={onCancel}
            className="p-1 rounded hover:bg-white/20"
            data-testid="map-preview-close"
            aria-label="Tutup"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Map */}
        <div className="relative flex-1 w-full" style={{ minHeight: 400 }}>
          <MapContainer
            key={`${pos.lat}-${pos.lng}-init`}
            center={[pos.lat, pos.lng]}
            zoom={19}
            maxZoom={21}
            scrollWheelZoom
            style={{ height: "100%", width: "100%", minHeight: 400, background: "#1a1a1a" }}
          >
            {layer === "satellite" ? (
              <>
                <TileLayer
                  attribution='&copy; Google'
                  url="https://mt{s}.google.com/vt/lyrs=y&x={x}&y={y}&z={z}"
                  subdomains={['0', '1', '2', '3']}
                  maxZoom={21}
                  maxNativeZoom={20}
                />
              </>
            ) : (
              <TileLayer
                attribution='&copy; Google'
                url="https://mt{s}.google.com/vt/lyrs=m&x={x}&y={y}&z={z}"
                subdomains={['0', '1', '2', '3']}
                maxZoom={21}
                maxNativeZoom={20}
              />
            )}
            <Marker
              position={[pos.lat, pos.lng]}
              draggable={!readonly}
              eventHandlers={readonly ? {} : { dragend: handleDragEnd }}
              ref={markerRef}
            />
            {/* Neighboring already-tagged houses — helps disambiguate dense bedeng */}
            {Array.isArray(nearby) && nearby.map((n) => (
              n?.latitude != null && n?.longitude != null ? (
                <Marker
                  key={`nb-${n.customer_id}`}
                  position={[n.latitude, n.longitude]}
                  icon={neighborIcon}
                  interactive
                >
                  <Popup>
                    <div className="text-xs leading-snug max-w-[220px]">
                      <div className="font-semibold text-red-700 mb-0.5">Sudah Tertandai</div>
                      <div><span className="text-slate-500">ID:</span> <span className="font-mono">{n.customer_id}</span></div>
                      {n.customer_name && <div><span className="text-slate-500">Nama:</span> {n.customer_name}</div>}
                      {n.customer_address && <div className="text-slate-600">{n.customer_address}</div>}
                      {n.distance_m != null && (
                        <div className="mt-1 text-[11px] text-slate-500">±{Math.round(n.distance_m)} m dari pin saat ini</div>
                      )}
                      {onDetailClick && (
                        <button
                          type="button"
                          onClick={() => onDetailClick(n)}
                          className="mt-1.5 text-[11px] text-cyan-700 hover:text-cyan-900 underline font-semibold"
                          data-testid={`neighbor-detail-${n.customer_id}`}
                        >
                          Lihat Detail »
                        </button>
                      )}
                    </div>
                  </Popup>
                </Marker>
              ) : null
            ))}
            <RecenterControl lat={pos.lat} lng={pos.lng} />
            <InvalidateOnMount />
            <FitAllControl
              trigger={fitAllNonce}
              coords={[
                { lat: pos.lat, lng: pos.lng },
                ...(Array.isArray(nearby) ? nearby.filter(n => n?.latitude != null && n?.longitude != null).map(n => ({ lat: n.latitude, lng: n.longitude })) : []),
              ]}
            />
            <ClickToMove
              onMove={({ lat, lng }) => setPos((prev) => ({ ...prev, lat, lng, accuracy: null }))}
              disabled={readonly}
            />
          </MapContainer>

          {/* Floating controls */}
          <div className="absolute top-2 right-2 z-[400] flex flex-col gap-2">
            <Button
              size="sm"
              variant="outline"
              className="bg-white/95 backdrop-blur shadow-md gap-1 h-9"
              onClick={() => setLayer((l) => (l === "satellite" ? "street" : "satellite"))}
              data-testid="toggle-layer-btn"
            >
              <Layers className="w-4 h-4" />
              {layer === "satellite" ? "Satelit" : "Jalan"}
            </Button>
            {!readonly && (
              <Button
                size="sm"
                variant="outline"
                className="bg-white/95 backdrop-blur shadow-md gap-1 h-9"
                onClick={useMyGPS}
                disabled={locking}
                data-testid="recenter-gps-btn"
                title="Tarik ulang posisi GPS saat ini"
              >
                {locking ? <Loader2 className="w-4 h-4 animate-spin" /> : <Crosshair className="w-4 h-4" />}
                GPS Saya
              </Button>
            )}
            {enableFitAll && Array.isArray(nearby) && nearby.length > 0 && (
              <Button
                size="sm"
                variant="outline"
                className="bg-white/95 backdrop-blur shadow-md gap-1 h-9"
                onClick={() => setFitAllNonce(n => n + 1)}
                data-testid="fit-all-btn"
                title="Zoom ke semua titik tertandai"
              >
                <Maximize2 className="w-4 h-4" />
                Fit Semua
              </Button>
            )}
          </div>
        </div>

        {/* Footer */}
        <div className="p-3 border-t border-slate-200 bg-slate-50 flex-shrink-0">
          {Array.isArray(nearby) && nearby.length > 0 && (
            <div className="mb-2 flex items-center gap-2 text-[11px] text-slate-600 bg-red-50 border border-red-200 rounded-md px-2 py-1">
              <span className="inline-block w-2.5 h-2.5 rounded-full bg-red-500 border-2 border-white shadow" />
              <span>
                <span className="font-semibold text-red-700">{nearby.length}</span> rumah sudah tertandai (titik merah). Tap titik untuk lihat ID & nama. Zoom out untuk lihat semua.
              </span>
            </div>
          )}
          <div className="text-[11px] text-slate-600 mb-2 font-mono break-all">
            <span className="text-slate-400">Koordinat:</span> {pos.lat?.toFixed(6)}, {pos.lng?.toFixed(6)}
            {pos.accuracy != null && (
              <span className="ml-2 text-emerald-600">±{Math.round(pos.accuracy)} m</span>
            )}
          </div>
          <div className="flex gap-2">
            <Button
              variant="outline"
              className="flex-1"
              onClick={onCancel}
              disabled={saving}
              data-testid="map-preview-cancel"
            >
              {readonly ? "Tutup" : "Batal"}
            </Button>
            {!readonly && (
              <Button
                className="flex-1 bg-emerald-600 hover:bg-emerald-700 text-white gap-1"
                onClick={() =>
                  onConfirm({
                    latitude: pos.lat,
                    longitude: pos.lng,
                    accuracy: pos.accuracy,
                  })
                }
                disabled={saving}
                data-testid="map-preview-confirm"
              >
                {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
                {confirmLabel}
              </Button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
